import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, PlayCircle, ArrowRight, GraduationCap, Sparkles } from "lucide-react";
import { applyOGTags } from "../lib/ogTags";

// Pool care educational videos — web-optimized (720p H.264, ~7MB each, fast-start).
const VIDEOS = [
  {
    id: "weekly-cleaning",
    title: "What a Weekly Pool Cleaning Looks Like",
    blurb:
      "A start-to-finish walk-through of your weekly visit — skim, brush, vacuum, baskets, test, balance, and photo report. Exactly what gets done, every single week.",
    src: "/videos/weekly-cleaning.mp4",
    poster: "/posters/weekly-cleaning.jpg",
    tag: "Weekly Routine",
  },
  {
    id: "filter-cleanings",
    title: "Filter Cleanings — Why They Save You Money",
    blurb:
      "Your filter is the kidney of your pool. Skip a cleaning and you're slowly killing your pump. Here's how often filters should be cleaned, what a dirty one looks like, and the signs you've waited too long.",
    src: "/videos/filter-cleanings.mp4",
    poster: "/posters/filter-cleanings.jpg",
    tag: "Equipment Care",
  },
  {
    id: "biggest-complaint",
    title: "The #1 Pool Owner Complaint",
    blurb:
      "The single most common frustration we hear from brand-new clients — and the simple fix that keeps your pool sparkling all year without surprise costs.",
    src: "/videos/biggest-complaint.mp4",
    poster: "/posters/biggest-complaint.jpg",
    tag: "Insider Tip",
  },
  {
    id: "salt-pool",
    title: "Salt Pool Care — What Every Owner Should Know",
    blurb:
      "Salt pool? It's not maintenance-free. Here's the routine your salt cell needs, the early warning signs of trouble, and how to keep your generator running for years.",
    src: "/videos/salt-pool.mp4",
    poster: "/posters/salt-pool.jpg",
    tag: "Salt Systems",
  },
  {
    id: "skimmer-baskets",
    title: "Skimmer Baskets — Why They Matter",
    blurb:
      "Your skimmer basket is the first line of defense for your pump and filter. Here's what it should look like, how often to empty it, and the cheap habit that prevents big repair bills.",
    src: "/videos/skimmer-baskets.mp4",
    poster: "/posters/skimmer-baskets.jpg",
    tag: "Equipment 101",
  },
  {
    id: "whos-in-your-backyard",
    title: "Who's In Your Backyard?",
    blurb:
      "You're handing a stranger access to your home every week. Here's what to look for in a pool tech you can actually trust — and the red flags that should send you running.",
    src: "/videos/whos-in-your-backyard.mp4",
    poster: "/posters/whos-in-your-backyard.jpg",
    tag: "Trust & Safety",
  },
];

function VideoCard({ video }) {
  return (
    <article
      className="group bg-white rounded-3xl overflow-hidden shadow-lg ring-1 ring-slate-100 hover:ring-brand-pink/30 hover:-translate-y-1 transition-all duration-300"
      data-testid={`pool-tips-card-${video.id}`}
    >
      <div className="relative aspect-video bg-brand-navy">
        <video
          src={video.src}
          poster={video.poster}
          controls
          preload="metadata"
          playsInline
          className="w-full h-full object-contain"
          data-testid={`pool-tips-video-${video.id}`}
        />
      </div>
      <div className="p-6 sm:p-7">
        <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-pink mb-3">
          <Sparkles className="w-3.5 h-3.5" /> {video.tag}
        </div>
        <h2 className="text-xl sm:text-2xl font-heading font-extrabold text-brand-navy mb-3 leading-tight">
          {video.title}
        </h2>
        <p className="text-slate-700 leading-relaxed text-base">{video.blurb}</p>
      </div>
    </article>
  );
}

export default function PoolTipsPage() {
  useEffect(() => {
    document.title =
      "Pool Tips & Care Videos | The Pool Guru Treasure Coast";
    applyOGTags({
      title: "Pool Tips & Care Videos | The Pool Guru Treasure Coast",
      description:
        "Free pool care videos from a 20+ year Treasure Coast pool pro. Green pool recovery, salt cell care, weekly service walkthroughs, and the biggest pool-owner mistakes.",
    });
  }, []);

  return (
    <div data-testid="pool-tips-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy to-[#0d2a4f]" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-brand-pink/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-blue/10 blur-3xl" />

        <div className="section-container relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full pl-3 pr-4 py-2 mb-6 ring-1 ring-white/20">
            <GraduationCap className="w-4 h-4 text-brand-pink" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">
              Free Pool Education
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-white leading-tight max-w-3xl mb-5 drop-shadow-lg">
            Pool Tips From the <span className="text-brand-pink">Pros</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed">
            Short, no-nonsense videos covering the things every pool owner on the
            Treasure Coast should know — from green pool recovery to salt cell
            care. Watch, learn, and call us when you&rsquo;d rather not DIY it.
          </p>
        </div>
      </section>

      {/* VIDEO GRID */}
      <section className="bg-slate-50 py-16 sm:py-24">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
            {VIDEOS.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16 sm:py-20">
        <div className="section-container max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-brand-navy mb-4">
            Rather Skip the DIY?
          </h2>
          <p className="text-lg text-slate-700 mb-8 leading-relaxed">
            Let the Pool Guru team take pool care off your plate. Weekly service,
            green-pool recovery, salt cell swaps, and equipment repair — all
            handled by a family-owned crew across the Treasure Coast.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:7722890405"
              className="btn-primary"
              data-testid="pool-tips-cta-phone"
            >
              <Phone className="w-4 h-4" /> Call 772-289-0405
            </a>
            <Link
              to="/contact"
              className="btn-outline"
              data-testid="pool-tips-cta-estimate"
            >
              Get a Free Estimate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Single inline-playable card used by the homepage teaser.
function InlineVideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      className="group bg-slate-50 rounded-2xl overflow-hidden ring-1 ring-slate-100 hover:ring-brand-pink/40 transition-all duration-300"
      data-testid={`home-pool-tips-card-${video.id}`}
    >
      <div
        className={
          "relative bg-brand-navy overflow-hidden " +
          (playing ? "aspect-[9/16] sm:aspect-video" : "aspect-video")
        }
      >
        {playing ? (
          <video
            src={video.src}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid={`home-pool-tips-video-${video.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${video.title}`}
            className="absolute inset-0 w-full h-full block cursor-pointer"
            data-testid={`home-pool-tips-play-${video.id}`}
          >
            <img
              src={video.poster}
              alt={video.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white/95 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                <PlayCircle className="w-8 h-8 text-brand-pink" />
              </div>
            </div>
            <div className="absolute top-3 left-3 bg-brand-pink text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
              {video.tag}
            </div>
          </button>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-lg font-heading font-bold text-brand-navy leading-snug">
          {video.title}
        </h3>
      </div>
    </div>
  );
}

// Reusable teaser strip for the homepage.
export function PoolTipsTeaser() {
  const featured = VIDEOS.slice(0, 3);
  return (
    <section
      className="bg-white py-16 sm:py-20 border-t border-slate-100"
      data-testid="home-pool-tips-teaser"
    >
      <div className="section-container">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-pink mb-3">
              <PlayCircle className="w-4 h-4" /> Pool Tips
            </div>
            <h2 className="text-3xl sm:text-5xl font-heading font-extrabold text-brand-navy leading-tight">
              Frequently Asked <span className="text-brand-pink">Questions</span>
            </h2>
            <p className="text-lg text-slate-700 mt-3 max-w-2xl">
              Quick answers to the questions Treasure Coast pool owners ask us most &mdash;
              from green pool recovery to salt cell maintenance.
            </p>
          </div>
          <Link
            to="/pool-tips"
            className="btn-outline self-start lg:self-auto"
            data-testid="home-pool-tips-cta"
          >
            See all videos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((v) => (
            <InlineVideoCard key={v.id} video={v} />
          ))}
        </div>
      </div>
    </section>
  );
}
